import React from 'react';
import LegalPageLayout from '../components/LegalPageLayout';

const QA = ({ q, children }) => (
  <div className="info-card">
    <h3 className="!mt-0">{q}</h3>
    {children}
  </div>
);

const SCAStatement = () => (
  <LegalPageLayout
    eyebrow="Compliance · Regulatory"
    title="Strong Customer Authentication (SCA) Statement"
    lead="eCOMM Merchant Solutions' statement on Strong Customer Authentication — the European regulatory requirement intended to reduce online fraud and make online payments more secure."
    testId="sca-statement-page"
  >
    <h2>Statement</h2>
    <p>
      From 14 September 2019, new rules were introduced that would affect the way banks or other
      payment services providers verify that the person requesting access to their account or
      trying to make a payment is the person permitted to make a payment and validate specific
      payment instructions. Due to industry concerns, the deadline for these rules was postponed
      to 31 December 2020 in the EU and 14 March 2021 in Denmark, France and the UK, with a
      phased rollout planned prior.
    </p>
    <p>
      The new rules — referred to as <strong>Strong Customer Authentication (SCA)</strong> — are
      a European regulatory requirement intended to reduce online fraud and make online payments
      more secure.
    </p>
    <p>
      These rules are set in the Second Payment Services Directive 2015/2366/EU (or PSD2),
      which applies to payment services in the EU. In the UK, PSD2 has been transposed into the
      legislation in the Payment Services Regulations 2017 (PSRs 2017). In Ireland, PSD2 became
      law on 13 January 2018 with the signing of the European Union (Payment Services)
      Regulations 2018.
    </p>
    <p>
      The original deadline for compliance with the Regulatory Technical Standards (RTS) on
      Strong Customer Authentication (SCA) under the PSD2 Directive was 14 September 2019. Due
      to industry concerns, the European Banking Authority (EBA) agreed to postpone the deadline
      until 31 December 2020. Prior to EBA's decision, regulatory authorities of Denmark, France
      and the UK had already established 18-month extensions until 14 March 2021.
    </p>

    <h2>Q&A</h2>

    <QA q="What is Strong Customer Authentication?">
      <p>
        Strong Customer Authentication (SCA) ensures that all remote electronic payments are
        performed with multi-factor authentication, to increase the security of electronic
        payments. SCA requirements apply to Point of Sale and e-Commerce transactions.
      </p>
      <p>SCA requires cardholder data from at least two of the following categories to be provided during the authentication process:</p>
      <h4>Something you know</h4>
      <ul>
        <li>Password</li>
        <li>PIN</li>
        <li>Secret fact</li>
      </ul>
      <h4>Something you own</h4>
      <ul>
        <li>Mobile phone</li>
        <li>Wearable device</li>
        <li>Token</li>
      </ul>
      <h4>Something you are</h4>
      <ul>
        <li>Fingerprint</li>
        <li>Facial feature</li>
        <li>Voice pattern</li>
      </ul>
    </QA>

    <QA q="Can I decide whether to apply SCA as I do with 3DS?">
      <p>
        No — you, the merchant, must apply SCA for all e-Commerce transactions unless they are
        out of scope. The card issuer will decide whether to challenge the cardholder for SCA.
        Even if a merchant flags a transaction as exempt, the issuer will have the final say and
        may require the transaction to be authenticated. This is called a challenge flow and
        requires additional cardholder data to be provided.
      </p>
    </QA>

    <QA q="Is SCA mandatory for all transactions?">
      <p>SCA is mandatory for all e-commerce transactions with a number of exclusions — out-of-scope transactions.</p>
    </QA>

    <QA q="What are the benefits of 3DS2?">
      <p>
        3DS2 allows businesses and their payment provider to submit additional data in each
        transaction to the cardholder's bank — the issuer. Frictionless processing through use
        of exemptions is also allowed, however SCA may still be required by the issuer.
      </p>
      <p>Regardless of the payment device type and payment channel, 3DS2 enables a better customer experience. It is also expected that 3DS2 will:</p>
      <ul>
        <li>Increase consumer confidence in an e-Commerce environment, resulting in a greater number of consumers buying online</li>
        <li>Reduce fraud and chargebacks, with fraud-related liability protection for merchants, when SCA is applied to a transaction</li>
        <li>Reduce abandonment rates due to a better user experience as the enhanced data flows will allow better decision making on a transaction and therefore potentially less challenge</li>
      </ul>
    </QA>

    <QA q="Which transactions are exempt from SCA?">
      <ul>
        <li><strong>Low value transactions &lt; €30.</strong> However, issuers will monitor these transactions and SCA will be required once the total value exceeds €100 and/or for every 5 transactions.</li>
        <li><strong>Low risk transactions</strong> — determined by the average fraud levels of the issuer and acquirer processing the transaction.</li>
        <li><strong>Recurring transactions</strong> with a fixed amount will be exempt although the first transaction requires SCA (see also Merchant Initiated Transactions below which are out of scope).</li>
        <li><strong>Trusted beneficiaries (white-listed merchants)</strong> — Issuers may provide cardholders with the option to assign businesses to a "whitelist" so that customers who shop with these businesses on a regular basis do not need SCA.</li>
        <li><strong>Secure Corporate Payments</strong> — B2B payments (between two businesses) using dedicated payment instruments designed for this purpose.</li>
      </ul>
      <p>For all exemptions, it is the issuer who makes the final decision. Therefore, if the acquirer/merchant requests an exemption, the final decision is in the issuer's hands.</p>
    </QA>

    <QA q="Which transactions are out of scope of SCA (exclusions)?">
      <ul>
        <li>
          <strong>"One Leg Out" (OLO) transactions</strong> — Transactions where the Payment
          Service Provider (PSP) of either the payer (i.e. the issuer) or of the payee (i.e. the
          acquirer) are located outside of the EEA.
        </li>
        <li>
          <strong>Mail Order / Telephone Order (MOTO) including Virtual Terminal (VT)</strong> —
          MOTO transactions are not considered to be electronic payments, and therefore are out
          of scope of the regulation.
        </li>
        <li>
          <strong>Merchant Initiated Transactions (MITs)</strong> — A series of payments with a
          fixed or variable amount that the merchant performs without direct involvement of the
          cardholder (e.g. subscriptions).
        </li>
        <li><strong>Anonymous Cards</strong> — e.g. anonymous prepaid card.</li>
      </ul>

      <h4>1. Anonymous prepaid cards</h4>
      <p>Payments made through the use of anonymous payment instruments, such as anonymous prepaid (e.g. gift) cards, are not subject to the obligation of strong customer authentication. The issuer is the only one able to identify this type of card. The acquirer will not be able to identify from the primary account number that the product is an anonymous product.</p>
      <p><strong>Action required:</strong> none — this is managed by the cardholder's issuing bank.</p>

      <h4>2. Mail Order / Telephone Order (MOTO)</h4>
      <p>Payments transacted by email or telephone are out of scope for SCA since they are not considered to be electronic payments.</p>
      <p><strong>Action required:</strong> ensure correct coding of your MOTO transactions for all cardholder purchase and payment scenarios.</p>

      <h4>3. "One-leg" transaction</h4>
      <p>SCA regulations apply only to transactions made entirely within the EEA. If issuer or acquirer is domiciled outside the EEA ("One-leg out"), no SCA mandates apply.</p>
      <p><strong>Action required:</strong> none from the merchant. Issuers and acquirers may require SCA to be applied to "one-leg" transactions.</p>

      <h4>4. Merchant Initiated Transactions (MIT)</h4>
      <p>SCA is required for the customer's first payment, at which point the cardholder agrees to the terms and conditions of later subsequent charges. These subsequent charges will be excluded from SCA, provided that the cardholder is not present in the check-out flow (off-session) at the time when the charge occurs. This category also includes subsequent recurring payments.</p>
      <p><strong>Action required:</strong> none from the merchant. The transactions will be flagged by eCOMM as a Recurring or MIT according to card scheme specifications / MIT framework in order to be approved by the issuer as not requiring SCA.</p>
      <p><em>Note: The initial transaction must be authenticated with a challenge (SCA) for the following recurring / MIT transactions to be processed successfully.</em></p>
    </QA>

    <QA q="What is the difference between SCA exemptions and exclusions (out-of-scope transactions)?">
      <p>
        An SCA exemption means that the acquirer/merchant requests an exemption (aiming to
        achieve a frictionless transaction without SCA) and the issuer then makes the decision
        whether SCA is required. If it is required, the issuer will trigger the authentication
        (challenge request) flow to authenticate the cardholder.
      </p>
      <p>
        An exclusion ("out of scope" transaction) does not require any authentication obligation
        and decision/flow, on the condition that it is flagged correctly.
      </p>
    </QA>

    <QA q="Does SCA apply to transactions taken over the telephone?">
      <p>No. Mail order and telephone order (MOTO) / Virtual Terminal (VT) transactions are not considered to be electronic payments, and so they are out of scope for SCA. Merchants should continue to process these as usual.</p>
    </QA>

    <QA q="What impact does SCA have on liability for e-commerce fraud-related disputes?">
      <p>
        When SCA is applied to a transaction, merchants/acquirers avail of protection in the
        event that a fraud-related dispute occurs. When SCA is not applied and the transaction
        results in a fraud-related dispute, it is the merchant/acquirer who is liable for the
        fraudulent transaction.
      </p>
      <p><strong>Note:</strong> 3DS protects against fraud-related disputes. It does not protect against all chargeback disputes (e.g. goods/service not being as described or non-delivery related disputes).</p>

      <p style={{ marginTop: '1.25rem' }}>
        The following diagram shows the merchant and issuer options and merchant–issuer
        liability for each option:
      </p>
      <div className="sca-liability-wrapper">
        <table className="sca-liability-table" data-testid="sca-liability-diagram">
          <thead>
            <tr>
              <th colSpan="2">Merchant Options</th>
              <th>Issuer Options</th>
              <th>Liability</th>
            </tr>
          </thead>
          <tbody>
            <tr>
              <td rowSpan="5" className="cell-light">Sends a 3DS2 authentication request and…</td>
              <td rowSpan="2" className="cell-light">…does <strong>not</strong> request Acquirer exemption</td>
              <td className="cell-mid">Requests SCA</td>
              <td rowSpan="2" className="cell-strong">Issuer</td>
            </tr>
            <tr>
              <td className="cell-mid">Does <strong>not</strong> request SCA</td>
            </tr>
            <tr>
              <td rowSpan="2" className="cell-light">…requests an Acquirer exemption</td>
              <td className="cell-mid">Requests SCA (Issuer step-up)</td>
              <td rowSpan="5" className="cell-strong">Acquirer / Merchant</td>
            </tr>
            <tr>
              <td className="cell-mid">Does <strong>not</strong> request SCA</td>
            </tr>
            <tr>
              <td className="cell-light">…sends data to the issuer to influence approvals, but does not perform an actual transaction with authorisation (data only)</td>
              <td className="cell-mid">Does <strong>not</strong> request SCA</td>
            </tr>
            <tr>
              <td colSpan="2" rowSpan="2" className="cell-light">Does <strong>not</strong> send a 3DS2 authentication request</td>
              <td className="cell-mid">Does <strong>not</strong> request SCA</td>
            </tr>
            <tr>
              <td className="cell-mid">Responds with a Soft Decline (RC 65), where then the transaction has to be resent with an authentication request (process re-starts)</td>
            </tr>
          </tbody>
        </table>
      </div>
    </QA>

    <QA q="What exemptions are available at eCOMM Merchant Solutions?">
      <p>eCOMM will support all acquirer exemptions subject to relevant risk policies and assessments, e.g. for the local market, specific business types, and individual merchant risk as applicable.</p>
    </QA>

    <QA q="Does the fraud rate for the TRA exemption apply to me as a merchant?">
      <p>No — it applies to the acquirer and issuer, depending on who wants to request the exemption.</p>
    </QA>

    <QA q="How do I go live with 3DS2?">
      <p>Direct API / HPP merchants may require the support of your web developer to make the necessary changes. eCOMM is not responsible for providing you with the necessary changes for the upgrade to 3DS v2.x. Should you fail to make the necessary changes, you will most likely see transactions getting declined.</p>
      <p>To provide the data required for 3DS2, merchants will need to make the necessary code changes to your gateway integration. The changes required will vary depending on your integration type and the business scenarios that you support. For the majority of merchants, the number of mandatory and recommended changes for standard payments are minimal.</p>
    </QA>

    <QA q="Do I have to include all the extra data in the 3DS2 request?">
      <p>All available data should be provided wherever possible to ensure an optimal cardholder and merchant experience and to reduce transaction friction (challenge rate). The more information you include, the greater the chance that the issuer will not challenge the cardholder with SCA, as it leads to a more informed decision-making process.</p>
    </QA>

    <QA q="Will SCA affect conversion rates of my card transactions?">
      <p>Whilst the benefit of SCA will be to reduce online card payment fraud levels, the changes may also affect conversion rates of people using their card online. <strong>If you do not implement the changes needed to support 3DS2 you will see an increase in declined transactions.</strong></p>
      <p>It is possible that it will take time for cardholders to become comfortable and familiar with the process. In the short term, SCA may lead to an increased number of abandoned transactions, with the number of transactions requiring authentication expected to decline over time. Ultimately 3DS2 is expected to increase consumer confidence in buying online by reducing fraud and abandonment rates due to enhanced data flows.</p>
    </QA>

    <p>
      If you have any queries, please contact our customer support team by emailing{' '}
      <a href="mailto:csr@ecomm365.com">csr@ecomm365.com</a>.
    </p>
  </LegalPageLayout>
);

export default SCAStatement;
